import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanLine, Heart, Trophy, Wallet, MessageCircle, QrCode, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const HERO_BG = "https://images.unsplash.com/photo-1709548145082-04d0cde481d4?w=1600&q=80";

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-[#F8F8F2] nod-grain overflow-x-hidden">
      {/* NAV */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0C]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2" data-testid="landing-logo">
            <span className="text-2xl md:text-3xl font-black tracking-tight">Nod</span>
            <span className="w-2 h-2 rounded-full bg-[#EBB300] gold-glow"></span>
            <span className="hidden sm:inline text-xs text-[#52525B] tracking-[0.2em] uppercase ml-2">Die Trinkgeld App</span>
          </Link>
          <nav className="flex items-center gap-2 md:gap-4 text-sm">
            <Link to="/scan" className="hidden md:inline text-[#A1A1AA] hover:text-white transition-colors" data-testid="landing-scan-link">QR scannen</Link>
            <Link to="/leaderboard" className="hidden md:inline text-[#A1A1AA] hover:text-white transition-colors" data-testid="landing-leaderboard-link">Leaderboard</Link>
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : user.role === "staff" ? "/staff" : "/app"}
                className="px-4 py-2 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold text-sm hover:bg-[#FAD02C] transition-colors" data-testid="landing-dashboard-link">
                Mein Bereich
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-[#A1A1AA] hover:text-white px-3 py-2 text-sm" data-testid="landing-login-link">Anmelden</Link>
                <Link to="/register" className="px-4 py-2 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold text-sm hover:bg-[#FAD02C] transition-colors gold-glow" data-testid="landing-register-link">
                  Starten
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/50 via-[#0A0A0C]/80 to-[#0A0A0C]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-32 pb-24 md:pb-40">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EBB300]/10 border border-[#EBB300]/30 text-[#EBB300] text-xs tracking-[0.2em] uppercase mb-8">
              <Sparkles className="w-3 h-3" /> 2026 Edition
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black leading-[0.95] tracking-tight max-w-5xl">
              Ein <span className="text-[#EBB300] gold-text-glow italic">Nicken</span><br/>
              ist genug.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#A1A1AA] max-w-2xl leading-relaxed">
              Trinkgeld digital, direkt und persönlich. Scan, tap, danke. Für Gäste, Service und Locations — mit Loyalty, Leaderboards und null Bargeld-Stress.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/scan" data-testid="hero-scan-button" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold text-base hover:bg-[#FAD02C] transition-all gold-glow">
                <ScanLine className="w-5 h-5" /> QR-Code scannen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/register" data-testid="hero-register-button" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-colors font-medium">
                Kostenlos registrieren
              </Link>
              <Link to="/leaderboard" className="text-sm text-[#A1A1AA] hover:text-white px-2 py-3 underline underline-offset-4 decoration-dotted" data-testid="hero-leaderboard-link">
                Live-Leaderboard ansehen
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Marquee */}
        <div className="relative z-10 border-y border-white/5 bg-[#0d0d11]/80 backdrop-blur overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap py-4">
            {[..."• Tap-to-Pay • Stempelkarten • Freeshots • QR pro Kellner • Multi-Location • Echtzeit-Feedback • ".split("•"), ..."• Tap-to-Pay • Stempelkarten • Freeshots • QR pro Kellner • Multi-Location • Echtzeit-Feedback • ".split("•")].map((t, i) => (
              <span key={i} className="text-[#A1A1AA] text-sm tracking-[0.2em] uppercase mx-6">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-6 md:gap-8">
          <div className="md:col-span-5">
            <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300] mb-4">So funktioniert es</div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight leading-tight">
              Drei Schritte.<br/>
              <span className="italic text-[#A1A1AA]">Eine Geste.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6">
            {[
              { n: "01", t: "Scannen", d: "Gast scannt den QR-Code am Tisch oder am Mitarbeiter-Badge." },
              { n: "02", t: "Wählen", d: "Aus dem Service-Team auswählen oder direkt zum Lieblings-Kellner." },
              { n: "03", t: "Nicken", d: "Betrag eingeben, Tap-to-Pay ausführen — fertig." },
            ].map((s) => (
              <div key={s.n} className="layered-card flex items-start gap-6 p-6 bg-[#121216] border border-white/5 rounded-2xl">
                <div className="font-black text-5xl text-[#EBB300]/20 leading-none">{s.n}</div>
                <div>
                  <div className="text-xl font-bold mb-1">{s.t}</div>
                  <p className="text-[#A1A1AA] leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24 md:pb-32">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300] mb-4">Für alle Seiten</div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">Drei Rollen, eine Plattform</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Heart, t: "Gäste", d: "QR scannen, Trinkgeld geben, Lieblings-Kellner speichern, Stempelkarten sammeln, Freeshots gewinnen.", cta: "/scan", label: "Jetzt scannen" },
            { icon: Wallet, t: "Service", d: "Eigenes Profil pflegen, persönliches Wallet, Nachrichten-Posteingang, eigene Position im Leaderboard.", cta: "/login", label: "Anmelden" },
            { icon: QrCode, t: "Location-Admin", d: "Locations & Mitarbeiter verwalten, QR-Codes generieren, Sichtbarkeit steuern, Statistiken auswerten.", cta: "/login", label: "Admin Login" },
          ].map((r) => (
            <div key={r.t} className="bg-[#121216] border border-white/5 rounded-2xl p-8 hover:border-[#EBB300]/30 transition-colors">
              <r.icon className="w-8 h-8 text-[#EBB300] mb-6" strokeWidth={1.5} />
              <h3 className="text-2xl font-bold mb-3">{r.t}</h3>
              <p className="text-[#A1A1AA] leading-relaxed mb-6 min-h-[80px]">{r.d}</p>
              <Link to={r.cta} className="inline-flex items-center gap-2 text-[#EBB300] text-sm font-semibold hover:gap-3 transition-all">
                {r.label} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* GAMIFICATION */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24 md:pb-32">
        <div className="relative rounded-3xl overflow-hidden border border-white/5 bg-gradient-to-br from-[#121216] to-[#0A0A0C] p-8 md:p-16">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#EBB300]/10 rounded-full blur-3xl"></div>
          <div className="relative grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#E05A33] mb-4">
                <Trophy className="w-4 h-4" /> Loyalty &amp; Gamification
              </div>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-6 leading-tight">
                Jedes Trinkgeld<br/>
                <span className="italic text-[#EBB300]">zählt doppelt.</span>
              </h2>
              <p className="text-[#A1A1AA] leading-relaxed mb-8">
                Stempelkarten füllen sich automatisch. Bei jedem fünften Tipp gibt es einen Freeshot. Service-Mitarbeiter*innen klettern im Leaderboard nach oben — mit echter Anerkennung statt Münzgeklimper.
              </p>
              <div className="flex gap-4">
                <div className="text-center">
                  <div className="text-5xl font-black text-[#EBB300]">5×</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mt-1">Stempel = Freeshot</div>
                </div>
                <div className="w-px bg-white/10"></div>
                <div className="text-center">
                  <div className="text-5xl font-black text-[#EBB300]">∞</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mt-1">Möglichkeiten</div>
                </div>
              </div>
            </div>
            <div className="relative">
              {/* Stamp card preview */}
              <div className="bg-[#0A0A0C] border border-white/10 rounded-2xl p-6 gold-glow">
                <div className="flex justify-between items-baseline mb-5">
                  <div className="font-bold tracking-tight">Deine Stempelkarte</div>
                  <div className="text-xs text-[#A1A1AA]">2/5</div>
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {[1, 1, 0, 0, 0].map((s, i) => (
                    <div key={i} className={`aspect-square rounded-full border-2 ${s ? "stamp-punched border-[#EBB300]" : "border-white/10 bg-[#121216]"}`}>
                      {s ? <div className="w-full h-full flex items-center justify-center text-[#0A0A0C]"><Sparkles className="w-4 h-4" /></div> : null}
                    </div>
                  ))}
                </div>
                <div className="ticket-divider my-5"></div>
                <p className="text-sm text-[#A1A1AA]">Noch <span className="text-[#EBB300] font-semibold">3 Trinkgelder</span> bis zum nächsten Freeshot 🥃</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-12 px-5 md:px-8 text-center text-sm text-[#52525B]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div>© 2026 Nod — Die Trinkgeld App.</div>
          <div className="flex gap-6">
            <Link to="/scan" className="hover:text-white">Scannen</Link>
            <Link to="/leaderboard" className="hover:text-white">Leaderboard</Link>
            <Link to="/login" className="hover:text-white">Anmelden</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
